import { computed, reactive, ref, toValue, watch, type MaybeRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { perspectivesFromTopic } from '@/core/models/perspective'
import type { Perspective } from '@/core/models/perspective'
import { useSnackStore } from '@/stores/snack.store'
import type { CoachFeedbackGoal } from './use-coach-feedback-goal'
import type { PerspectiveTarget } from './use-create-coach-feedback-goal'

type ErrorKey = 'perspectiveCounts'

type PutCoachFeedbackGoalPayload = {
  feedbackTopicId: string
  coacheeIds: string[]
  perspectiveTargets: PerspectiveTarget[]
  isActive: boolean
}

export const useEditCoachFeedbackGoal = (
  feedbackGoal: MaybeRef<CoachFeedbackGoal>,
  isOpen: Ref<boolean>,
  onClose?: () => void,
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const form = reactive<{ perspectiveCounts: Record<string, number> }>({
    perspectiveCounts: {},
  })

  const errors = ref<Map<ErrorKey, string>>(new Map())

  const goalPerspectives = computed((): Perspective[] => {
    const topic = toValue(feedbackGoal).feedbackTopic
    return perspectivesFromTopic(topic)
  })

  const getPerspectiveCount = (p: Perspective): number => form.perspectiveCounts[p.key] ?? 0

  const adjustPerspectiveCount = (p: Perspective, action: 'add' | 'remove') => {
    const current = form.perspectiveCounts[p.key] ?? 0
    if (action === 'add') {
      form.perspectiveCounts[p.key] = current + 1
    } else if (current > 0) {
      form.perspectiveCounts[p.key] = current - 1
    }
    errors.value = new Map()
  }

  const setPerspectiveCount = (p: Perspective, value: number) => {
    form.perspectiveCounts[p.key] = Math.max(0, Math.trunc(Number(value)) || 0)
    errors.value = new Map()
  }

  const buildPerspectiveTargets = (): PerspectiveTarget[] =>
    goalPerspectives.value.map((p) => ({
      perspectiveKey: p.key,
      target: form.perspectiveCounts[p.key] ?? 0,
    }))

  const validateStep = () => {
    errors.value = new Map()

    for (const p of goalPerspectives.value) {
      const n = form.perspectiveCounts[p.key]
      if (!Number.isInteger(n) || n < 0) {
        errors.value.set(
          'perspectiveCounts',
          t('feedback.formValidation.feedbackGoalPerspectiveCount'),
        )
        return
      }
    }
  }

  const resetFromGoal = () => {
    const goal = toValue(feedbackGoal)
    const next: Record<string, number> = {}
    for (const p of perspectivesFromTopic(goal.feedbackTopic)) {
      next[p.key] =
        goal.perspectiveTargets.find((t) => t.perspectiveKey === p.key)?.target ?? 0
    }
    form.perspectiveCounts = next
    errors.value = new Map()
  }

  const { mutate: updateGoal, status: updateStatus } = usePut<void, PutCoachFeedbackGoalPayload>(
    `coaching/feedback-goals/${toValue(feedbackGoal).id}`,
  )

  const saveGoal = async () => {
    validateStep()
    if (errors.value.size > 0) {
      return
    }

    await updateGoal({
      feedbackTopicId: toValue(feedbackGoal).feedbackTopic.id,
      coacheeIds: toValue(feedbackGoal).coachees.map((item) => item.coachee.id),
      perspectiveTargets: buildPerspectiveTargets(),
      isActive: true,
    })

    if (updateStatus.value === ApiRequestStatus.Success) {
      onClose?.()
      snackStore.showInfo(t('coaching.feedbackGoals.editSuccess'))
      return
    }

    snackStore.showError(t('coaching.feedbackGoals.editError'))
  }

  watch(isOpen, (open) => {
    if (open) {
      resetFromGoal()
    }
  })

  const isSaving = computed(() => updateStatus.value === ApiRequestStatus.Loading)

  return {
    saveGoal,
    form,
    errors,
    goalPerspectives,
    getPerspectiveCount,
    adjustPerspectiveCount,
    setPerspectiveCount,
    resetFromGoal,
    isSaving,
  }
}
